import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Plus, Trash2, Link as LinkIcon, Zap, Upload, Image, Paperclip, CheckSquare, Square, FileText, Film, Youtube } from "lucide-react";
import { FORMULA_TYPES } from "@/utils/calculations";
import axios from "axios";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

export default function PropertiesPanel({ node, onUpdate, onDelete, onClose, onDuplicate, onRecalculate }) {
  const [newTag, setNewTag] = useState("");
  const [newFieldKey, setNewFieldKey] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");
  const [newLink, setNewLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const attachInputRef = useRef(null);
  const [newContentItem, setNewContentItem] = useState("");
  const [newYoutubeUrl, setNewYoutubeUrl] = useState("");
  const [newLegendLabel, setNewLegendLabel] = useState("");
  const [newLegendColor, setNewLegendColor] = useState("#6366F1");

  const data = node.data;

  const update = (key, value) => {
    onUpdate(node.id, { [key]: value });
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    update("tags", [...(data.tags || []), newTag.trim()]);
    setNewTag("");
  };

  const removeTag = (index) => {
    update(
      "tags",
      (data.tags || []).filter((_, i) => i !== index)
    );
  };

  const addCustomField = () => {
    if (!newFieldKey.trim()) return;
    update("customFields", [
      ...(data.customFields || []),
      { key: newFieldKey, value: newFieldValue },
    ]);
    setNewFieldKey("");
    setNewFieldValue("");
  };

  const removeCustomField = (index) => {
    update(
      "customFields",
      (data.customFields || []).filter((_, i) => i !== index)
    );
  };

  const addLink = () => {
    if (!newLink.trim()) return;
    update("links", [...(data.links || []), newLink.trim()]);
    setNewLink("");
  };

  const removeLink = (index) => {
    update(
      "links",
      (data.links || []).filter((_, i) => i !== index)
    );
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: resp } = await axios.post(`${API}/upload/image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("imageUrl", resp.url);
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: resp } = await axios.post(`${API}/upload/file`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const attachments = data.attachments || [];
      update("attachments", [...attachments, {
        url: resp.url,
        name: resp.original_name || file.name,
        type: resp.file_type,
      }]);
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const addYoutubeLink = () => {
    const url = newYoutubeUrl.trim();
    if (!url) return;
    const attachments = data.attachments || [];
    update("attachments", [...attachments, { url, name: "YouTube", type: "youtube" }]);
    setNewYoutubeUrl("");
  };

  const removeAttachment = (idx) => {
    update("attachments", (data.attachments || []).filter((_, i) => i !== idx));
  };

  const addContentItem = () => {
    if (!newContentItem.trim()) return;
    const items = data.contentItems || [];
    update("contentItems", [...items, { label: newContentItem.trim(), done: false }]);
    setNewContentItem("");
  };

  const toggleContentItem = (idx) => {
    const items = [...(data.contentItems || [])];
    items[idx] = { ...items[idx], done: !items[idx].done };
    update("contentItems", items);
  };

  const removeContentItem = (idx) => {
    update("contentItems", (data.contentItems || []).filter((_, i) => i !== idx));
  };

  return (
    <div
      className="w-80 bg-white/80 backdrop-blur-sm border-l border-gray-200 h-full overflow-y-auto p-5 flex flex-col gap-5 shrink-0"
      data-testid="properties-panel"
    >
      <div className="flex justify-between items-center">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Propriétés
        </h3>
        <button
          onClick={onClose}
          className="w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
          data-testid="close-properties-btn"
        >
          <X size={14} />
        </button>
      </div>

      {/* Title */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Titre
        </Label>
        <Input
          value={data.title || ""}
          onChange={(e) => update("title", e.target.value)}
          className="rounded-lg border-gray-200 text-sm"
          data-testid="prop-title-input"
        />
      </div>

      {/* Amount */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Montant
        </Label>
        <Input
          value={data.amount || ""}
          onChange={(e) => update("amount", e.target.value)}
          className="rounded-lg border-gray-200 text-sm font-mono"
          placeholder="ex: 1 000 000 EUR"
          data-testid="prop-amount-input"
        />
      </div>

      {/* Description */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Description
        </Label>
        <textarea
          value={data.description || ""}
          onChange={(e) => update("description", e.target.value)}
          className="min-h-[70px] rounded-lg border border-gray-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none"
          data-testid="prop-description-input"
        />
      </div>

      {/* Condition (scénario nodes) */}
      {node.type === "scenario" && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-[10px] font-semibold text-rose-400 uppercase tracking-wider">
            Condition (si...)
          </Label>
          <Input
            value={data.condition || ""}
            onChange={(e) => update("condition", e.target.value)}
            className="rounded-lg border-rose-200 text-sm focus:ring-rose-500/20 focus:border-rose-500"
            placeholder="ex: CA > 1M EUR"
            data-testid="prop-condition-input"
          />
        </div>
      )}

      {/* Info block: list items */}
      {node.type === "info" && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wider">
            Liste d'elements
          </Label>
          {(data.listItems || []).map((item, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs bg-gray-50 rounded-lg px-2.5 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
              <input
                value={item}
                onChange={(e) => {
                  const items = [...(data.listItems || [])];
                  items[i] = e.target.value;
                  update("listItems", items);
                }}
                className="flex-1 bg-transparent text-gray-700 text-xs focus:outline-none"
                data-testid={`prop-list-item-${i}`}
              />
              <button
                onClick={() => update("listItems", (data.listItems || []).filter((_, j) => j !== i))}
                className="hover:text-rose-500 text-gray-300 shrink-0"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <div className="flex gap-1.5">
            <Input
              value={newContentItem}
              onChange={(e) => setNewContentItem(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newContentItem.trim()) {
                  update("listItems", [...(data.listItems || []), newContentItem.trim()]);
                  setNewContentItem("");
                }
              }}
              className="rounded-lg border-gray-200 text-xs flex-1"
              placeholder="Nouvel element..."
              data-testid="prop-list-new-item"
            />
            <button
              onClick={() => {
                if (newContentItem.trim()) {
                  update("listItems", [...(data.listItems || []), newContentItem.trim()]);
                  setNewContentItem("");
                }
              }}
              className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-400 shrink-0"
              data-testid="prop-add-list-item-btn"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Price block: HT & TVA */}
      {node.type === "price" && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">
            Tarification
          </Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <span className="text-[9px] text-gray-400 block mb-1">Prix HT (EUR)</span>
              <Input
                type="number"
                value={data.priceHT || ""}
                onChange={(e) => update("priceHT", e.target.value)}
                className="rounded-lg border-gray-200 text-sm font-mono"
                placeholder="1000"
                data-testid="prop-price-ht"
              />
            </div>
            <div className="w-20">
              <span className="text-[9px] text-gray-400 block mb-1">TVA %</span>
              <Input
                type="number"
                value={data.tvaRate || ""}
                onChange={(e) => update("tvaRate", parseFloat(e.target.value) || 0)}
                className="rounded-lg border-gray-200 text-sm font-mono"
                placeholder="20"
                data-testid="prop-tva-rate"
              />
            </div>
          </div>
          {data.priceHT && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-center">
              <span className="text-lg font-bold font-mono text-emerald-700">
                {(parseFloat(data.priceHT) * (1 + (data.tvaRate || 20) / 100)).toFixed(2)} EUR
              </span>
              <span className="text-[9px] text-emerald-500 block">TTC</span>
            </div>
          )}
        </div>
      )}

      {/* Legend items editor */}
      {node.type === "legend" && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-[10px] font-semibold text-violet-500 uppercase tracking-wider">
            Elements de la legende
          </Label>
          {(data.legendItems || []).map((item, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2 py-1.5">
              <input
                type="color"
                value={item.color || "#6366F1"}
                onChange={(e) => {
                  const items = [...(data.legendItems || [])];
                  items[i] = { ...items[i], color: e.target.value };
                  update("legendItems", items);
                }}
                className="w-6 h-6 rounded border-0 cursor-pointer p-0"
              />
              <input
                value={item.label || ""}
                onChange={(e) => {
                  const items = [...(data.legendItems || [])];
                  items[i] = { ...items[i], label: e.target.value };
                  update("legendItems", items);
                }}
                className="flex-1 bg-transparent text-xs text-gray-700 focus:outline-none"
                placeholder="Label..."
                data-testid={`legend-item-${i}`}
              />
              <button
                onClick={() => update("legendItems", (data.legendItems || []).filter((_, j) => j !== i))}
                className="hover:text-rose-500 text-gray-300 shrink-0"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <div className="flex gap-1.5">
            <input
              type="color"
              value={newLegendColor}
              onChange={(e) => setNewLegendColor(e.target.value)}
              className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0"
            />
            <Input
              value={newLegendLabel}
              onChange={(e) => setNewLegendLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newLegendLabel.trim()) {
                  update("legendItems", [...(data.legendItems || []), { color: newLegendColor, label: newLegendLabel.trim() }]);
                  setNewLegendLabel("");
                }
              }}
              className="rounded-lg border-gray-200 text-xs flex-1"
              placeholder="Nouveau label..."
              data-testid="legend-new-item"
            />
            <button
              onClick={() => {
                if (newLegendLabel.trim()) {
                  update("legendItems", [...(data.legendItems || []), { color: newLegendColor, label: newLegendLabel.trim() }]);
                  setNewLegendLabel("");
                }
              }}
              className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-400 shrink-0"
              data-testid="legend-add-btn"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Color */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Couleur
        </Label>
        <div className="flex gap-2">
          {[
            "#6366F1",
            "#3B82F6",
            "#F59E0B",
            "#F43F5E",
            "#10B981",
            "#8B5CF6",
            "#1F2937",
          ].map((c) => (
            <button
              key={c}
              onClick={() => update("color", c)}
              className={`w-7 h-7 rounded-full transition-all ${
                data.color === c
                  ? "ring-2 ring-offset-2 ring-gray-400 scale-110"
                  : "hover:scale-110"
              }`}
              style={{ backgroundColor: c }}
              data-testid={`prop-color-${c.replace("#", "")}`}
            />
          ))}
        </div>
      </div>

      {/* Font size & card width controls */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Taille
        </Label>
        <div className="flex gap-2">
          <div className="flex-1">
            <span className="text-[9px] text-gray-400 block mb-1">Texte</span>
            <div className="flex items-center gap-1">
              <button onClick={() => update("fontSize", Math.max(9, (data.fontSize || 13) - 1))} className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 flex items-center justify-center text-xs font-bold" data-testid="font-decrease">A-</button>
              <span className="text-[10px] font-mono text-gray-600 w-6 text-center">{data.fontSize || 13}</span>
              <button onClick={() => update("fontSize", Math.min(24, (data.fontSize || 13) + 1))} className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 flex items-center justify-center text-xs font-bold" data-testid="font-increase">A+</button>
            </div>
          </div>
          <div className="flex-1">
            <span className="text-[9px] text-gray-400 block mb-1">Largeur carte</span>
            <div className="flex items-center gap-1">
              <button onClick={() => update("nodeWidth", Math.max(180, (data.nodeWidth || 288) - 20))} className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 flex items-center justify-center text-xs" data-testid="width-decrease">-</button>
              <span className="text-[10px] font-mono text-gray-600 w-8 text-center">{data.nodeWidth || 288}</span>
              <button onClick={() => update("nodeWidth", Math.min(500, (data.nodeWidth || 288) + 20))} className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 flex items-center justify-center text-xs" data-testid="width-increase">+</button>
            </div>
          </div>
        </div>
      </div>


      {/* Image upload (for logo/image nodes) */}
      {(node.type === "logo" || node.type === "company" || node.type === "service") && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
            <Image size={10} /> Image / Logo
          </Label>
          {data.imageUrl ? (
            <div className="relative">
              <img
                src={data.imageUrl.startsWith("/api") ? `${API.replace("/api", "")}${data.imageUrl}` : data.imageUrl}
                alt="Logo"
                className="w-full h-20 object-contain rounded-lg border border-gray-200 bg-gray-50"
              />
              <button
                onClick={() => update("imageUrl", undefined)}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center hover:bg-rose-600"
              >
                <X size={10} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors disabled:opacity-50"
              data-testid="upload-image-btn"
            >
              <Upload size={16} className="mx-auto mb-1 text-gray-400" />
              <span className="text-[10px] text-gray-400 block">
                {uploading ? "Envoi..." : "Choisir une image"}
              </span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>
      )}

      {/* Calcul dynamique */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-violet-500 uppercase tracking-wider flex items-center gap-1">
          <Zap size={10} /> Calcul dynamique
        </Label>
        {data.formula?.type ? (
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-violet-700 flex items-center gap-1">
                <span className="text-lg">{FORMULA_TYPES.find(f => f.type === data.formula.type)?.icon}</span>
                {FORMULA_TYPES.find(f => f.type === data.formula.type)?.label}
              </span>
              <button
                onClick={() => { update("formula", undefined); update("calculatedAmount", undefined); setTimeout(() => onRecalculate?.(), 50); }}
                className="text-violet-400 hover:text-rose-500"
              >
                <X size={12} />
              </button>
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={() => { update("formula", { ...data.formula, direction: "in" }); setTimeout(() => onRecalculate?.(), 50); }}
                className={`flex-1 text-[10px] py-1 rounded-md font-medium ${data.formula.direction !== "out" ? "bg-violet-600 text-white" : "bg-white text-violet-600 border border-violet-200"}`}
              >
                Entrants
              </button>
              <button
                onClick={() => { update("formula", { ...data.formula, direction: "out" }); setTimeout(() => onRecalculate?.(), 50); }}
                className={`flex-1 text-[10px] py-1 rounded-md font-medium ${data.formula.direction === "out" ? "bg-violet-600 text-white" : "bg-white text-violet-600 border border-violet-200"}`}
              >
                Sortants
              </button>
            </div>
            {data.formula.type === "PERCENT" && (
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  value={data.formula.percent || ""}
                  onChange={(e) => { update("formula", { ...data.formula, percent: parseFloat(e.target.value) || 0 }); setTimeout(() => onRecalculate?.(), 50); }}
                  className="rounded-md border-violet-200 text-xs w-20"
                  placeholder="80"
                />
                <span className="text-xs text-violet-500">% du parent</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Input
                value={data.formula.suffix || ""}
                onChange={(e) => { update("formula", { ...data.formula, suffix: e.target.value }); setTimeout(() => onRecalculate?.(), 50); }}
                className="rounded-md border-violet-200 text-xs flex-1"
                placeholder="Suffixe (ex: EUR, %, unites)"
              />
            </div>
            {data.calculatedAmount && (
              <div className="bg-white rounded-md p-2 text-center">
                <span className="text-lg font-bold font-mono text-violet-700">{data.calculatedAmount}</span>
                <span className="text-[10px] text-violet-400 block">Résultat dynamique</span>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1">
            {FORMULA_TYPES.map((f) => (
              <button
                key={f.type}
                onClick={() => { update("formula", { type: f.type, direction: "in", suffix: "", percent: f.type === "PERCENT" ? 80 : undefined }); setTimeout(() => onRecalculate?.(), 100); }}
                className="bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg p-2 text-center transition-colors"
                title={f.description}
                data-testid={`formula-${f.type}`}
              >
                <span className="text-base block">{f.icon}</span>
                <span className="text-[9px] text-violet-600 font-medium">{f.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tags */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Tags
        </Label>
        <div className="flex flex-wrap gap-1 mb-1">
          {(data.tags || []).map((tag, i) => (
            <span
              key={i}
              className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 flex items-center gap-1"
            >
              {tag}
              <button
                onClick={() => removeTag(i)}
                className="hover:text-rose-500"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-1.5">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTag()}
            className="rounded-lg border-gray-200 text-xs flex-1"
            placeholder="Nouveau tag"
            data-testid="prop-tag-input"
          />
          <button
            onClick={addTag}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-400 shrink-0"
            data-testid="prop-add-tag-btn"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Custom Fields */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Champs personnalises
        </Label>
        {(data.customFields || []).map((field, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs bg-gray-50 rounded-lg px-2.5 py-1.5">
            <span className="text-gray-400 flex-1 truncate">
              {field.key}:
            </span>
            <span className="font-semibold text-gray-700 flex-1 truncate">{field.value}</span>
            <button
              onClick={() => removeCustomField(i)}
              className="hover:text-rose-500 text-gray-300 shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <div className="flex gap-1.5">
          <Input
            value={newFieldKey}
            onChange={(e) => setNewFieldKey(e.target.value)}
            className="rounded-lg border-gray-200 text-xs flex-1"
            placeholder="Cle"
            data-testid="prop-field-key-input"
          />
          <Input
            value={newFieldValue}
            onChange={(e) => setNewFieldValue(e.target.value)}
            className="rounded-lg border-gray-200 text-xs flex-1"
            placeholder="Valeur"
            data-testid="prop-field-value-input"
          />
          <button
            onClick={addCustomField}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-400 shrink-0"
            data-testid="prop-add-field-btn"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Links */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Liens
        </Label>
        {(data.links || []).map((link, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs">
            <LinkIcon size={10} className="text-gray-300 shrink-0" />
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="text-indigo-500 hover:text-indigo-700 underline truncate flex-1"
            >
              {link}
            </a>
            <button
              onClick={() => removeLink(i)}
              className="hover:text-rose-500 text-gray-300 shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <div className="flex gap-1.5">
          <Input
            value={newLink}
            onChange={(e) => setNewLink(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addLink()}
            className="rounded-lg border-gray-200 text-xs flex-1"
            placeholder="https://..."
            data-testid="prop-link-input"
          />
          <button
            onClick={addLink}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-400 shrink-0"
            data-testid="prop-add-link-btn"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-auto flex flex-col gap-2">
        {onDuplicate && (
          <button
            onClick={() => onDuplicate(node.id)}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-1.5"
            data-testid="duplicate-node-btn"
          >
            Dupliquer (Ctrl+D)
          </button>
        )}

        {/* ─── PIECES JOINTES ─── */}
        <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-3">
          <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
            <Paperclip size={10} /> Pièces jointes
          </Label>
          {(data.attachments || []).map((att, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2.5 py-1.5 group">
              {att.type === "youtube" ? <Youtube size={12} className="text-rose-500 shrink-0" /> :
               att.type === "image" ? <Image size={12} className="text-blue-500 shrink-0" /> :
               att.type === "pdf" ? <FileText size={12} className="text-rose-600 shrink-0" /> :
               <Paperclip size={12} className="text-gray-400 shrink-0" />}
              <a href={att.url.startsWith("/api") ? `${API.replace("/api", "")}${att.url}` : att.url} target="_blank" rel="noreferrer" className="text-[10px] text-indigo-600 truncate flex-1 hover:underline">{att.name}</a>
              <button onClick={() => removeAttachment(i)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-rose-500 transition-opacity"><X size={10} /></button>
            </div>
          ))}
          <div className="flex gap-1.5">
            <button onClick={() => attachInputRef.current?.click()} disabled={uploading} className="flex-1 border border-dashed border-gray-200 rounded-lg py-1.5 text-[10px] text-gray-400 hover:border-indigo-300 hover:text-indigo-500 transition-colors flex items-center justify-center gap-1 disabled:opacity-50">
              <Upload size={10} /> {uploading ? "Envoi..." : "Fichier"}
            </button>
            <input ref={attachInputRef} type="file" onChange={handleAttachmentUpload} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv" />
          </div>
          <div className="flex gap-1">
            <Input value={newYoutubeUrl} onChange={(e) => setNewYoutubeUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addYoutubeLink()} placeholder="Lien YouTube..." className="text-[10px] h-7 rounded-md border-gray-200" />
            <button onClick={addYoutubeLink} disabled={!newYoutubeUrl.trim()} className="shrink-0 w-7 h-7 rounded-md bg-rose-50 text-rose-500 flex items-center justify-center hover:bg-rose-100 disabled:opacity-30"><Film size={10} /></button>
          </div>
        </div>

        {/* ─── CONTENUS ATTENDUS ─── */}
        <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-3">
          <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
            <CheckSquare size={10} /> Contenus attendus
            {(data.contentItems || []).length > 0 && (
              <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                (data.contentItems || []).every(c => c.done) ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
              }`}>
                {(data.contentItems || []).filter(c => c.done).length}/{(data.contentItems || []).length}
              </span>
            )}
          </Label>
          {(data.contentItems || []).map((item, i) => (
            <div key={i} className="flex items-center gap-2 group">
              <button onClick={() => toggleContentItem(i)} className="shrink-0">
                {item.done ? <CheckSquare size={14} className="text-emerald-500" /> : <Square size={14} className="text-gray-300" />}
              </button>
              <span className={`text-xs flex-1 ${item.done ? "text-gray-400 line-through" : "text-gray-700"}`}>{item.label}</span>
              <button onClick={() => removeContentItem(i)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-rose-500 transition-opacity"><X size={10} /></button>
            </div>
          ))}
          <div className="flex gap-1">
            <Input value={newContentItem} onChange={(e) => setNewContentItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addContentItem()} placeholder="Ajouter un contenu requis..." className="text-[10px] h-7 rounded-md border-gray-200" />
            <button onClick={addContentItem} disabled={!newContentItem.trim()} className="shrink-0 w-7 h-7 rounded-md bg-indigo-50 text-indigo-500 flex items-center justify-center hover:bg-indigo-100 disabled:opacity-30"><Plus size={10} /></button>
          </div>
        </div>

        <button
          onClick={() => onDelete(node.id)}
          className="bg-rose-500 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-rose-600 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
          data-testid="delete-node-btn"
        >
          <Trash2 size={14} /> Supprimér cet élément
        </button>
      </div>
    </div>
  );
}
